// src/pages/Aide.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  Wrench, 
  Calendar, 
  Users, 
  Truck, 
  HelpCircle, 
  FileText,
  Download,
  Search,
  Filter,
  CheckCircle,
  Clock,
  Settings,
  BarChart,
  Home,
  Mail,
  Phone,
  BookOpen,
  Video,
  AlertCircle
} from 'lucide-react';

const Aide = () => {
  const modules = [
    {
      id: 1,
      icon: <Package className="w-6 h-6" />,
      title: "Gestion de Stock",
      description: "Gestion des matériels, demandes et stocks",
      color: "blue",
      sections: [
        {
          title: "Demander du matériel",
          steps: [
            "Accédez à 'Nouvelle demande' dans Gestion de stock",
            "Sélectionnez catégorie et matériel",
            "Indiquez la quantité nécessaire",
            "Soumettez au validateur"
          ]
        },
        {
          title: "Valider une demande",
          steps: [
            "Vérifiez la disponibilité en stock",
            "Acceptez, refusez ou modifiez les quantités",
            "Ajoutez un commentaire si nécessaire"
          ]
        }
      ]
    },
    {
      id: 2,
      icon: <Wrench className="w-6 h-6" />,
      title: "Réparations Véhicules",
      description: "Suivi des interventions et maintenance",
      color: "green",
      sections: [
        {
          title: "Déclarer une panne",
          steps: [
            "Sélectionnez le véhicule concerné",
            "Décrivez le problème rencontré",
            "Indiquez le kilométrage actuel"
          ]
        },
        {
          title: "Suivre une réparation",
          steps: [
            "Consultez le statut dans 'Mes demandes'",
            "Recevez des notifications à chaque étape",
            "Consultez devis et pièces utilisées"
          ]
        }
      ]
    },
    {
      id: 3,
      icon: <Calendar className="w-6 h-6" />,
      title: "Événements",
      description: "Organisation et logistique événementielle",
      color: "purple",
      sections: [
        {
          title: "Planifier un événement",
          steps: [
            "Choisissez 'Nouvel événement'",
            "Sélectionnez le type et bénéficiaire",
            "Sélectionnez le matériel nécessaire"
          ]
        },
        {
          title: "Gérer les enterrements",
          steps: [
            "Sélectionnez le pack adapté",
            "Indiquez les besoins spécifiques",
            "Suivez la préparation en temps réel"
          ]
        }
      ]
    },
    {
      id: 4,
      icon: <Users className="w-6 h-6" />,
      title: "Congés",
      description: "Gestion des absences et planning",
      color: "orange",
      sections: [
        {
          title: "Demander un congé",
          steps: [
            "Vérifiez votre solde disponible",
            "Sélectionnez les dates souhaitées",
            "Soumettez à votre supérieur"
          ]
        },
        {
          title: "Valider les demandes",
          steps: [
            "Vérifiez les chevauchements d'équipe",
            "Consultez le planning global",
            "Acceptez ou refusez avec motif"
          ]
        }
      ]
    },
    {
      id: 5,
      icon: <Truck className="w-6 h-6" />,
      title: "Déplacements",
      description: "Gestion du parc automobile et missions",
      color: "red",
      sections: [
        {
          title: "Demander un déplacement",
          steps: [
            "Indiquez destination et date",
            "Listez les personnes à transporter",
            "Soumettez pour validation"
          ]
        },
        {
          title: "Gestion des véhicules",
          steps: [
            "Consultez la disponibilité",
            "Affectez les chauffeurs",
            "Planifiez les entretiens"
          ]
        }
      ]
    }
  ];

  const quickTips = [
    {
      icon: <Search className="w-5 h-5" />,
      text: "Utilisez les filtres pour trouver rapidement vos informations"
    },
    {
      icon: <Filter className="w-5 h-5" />,
      text: "Personnalisez vos vues avec les options de tri"
    },
    {
      icon: <Download className="w-5 h-5" />,
      text: "Exportez vos données en Excel ou PDF"
    },
    {
      icon: <Clock className="w-5 h-5" />,
      text: "Session expire après 30 minutes d'inactivité"
    }
  ];

  const faqs = [
    {
      question: "Comment réinitialiser mon mot de passe ?",
      answer: "Cliquez sur 'Mot de passe oublié' à la page de connexion ou contactez l'administrateur."
    },
    {
      question: "Comment modifier mes informations ?",
      answer: "Accédez à 'Mon profil' depuis le menu utilisateur."
    },
    {
      question: "Qui peut voir mes demandes ?",
      answer: "Seuls votre supérieur et les validateurs concernés."
    },
    {
      question: "Comment imprimer un bon de livraison ?",
      answer: "Dans la fiche validée, cliquez sur 'Imprimer'."
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: 'bg-blue-100 text-blue-600' },
      green: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: 'bg-green-100 text-green-600' },
      purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: 'bg-purple-100 text-purple-600' },
      orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: 'bg-orange-100 text-orange-600' },
      red: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: 'bg-red-100 text-red-600' }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Link
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-gray-900 text-sm"
            >
              <Home className="w-4 h-4 mr-2" />
              Accueil
            </Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-900 font-medium">Centre d'aide</span>
          </div>
          
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Centre d'aide</h1>
            <p className="text-gray-600 mt-2">
              Documentation complète pour l'utilisation de la plateforme
            </p>
          </div>
        </div>

        {/* Modules Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {modules.map((module) => {
            const colorClasses = getColorClasses(module.color);
            return (
              <div key={module.id} className="bg-white rounded-lg border hover:shadow-md transition-shadow">
                <div className={`px-5 py-4 border-b`}>
                  <div className="flex items-center">
                    <div className={`w-10 h-10 rounded flex items-center justify-center mr-3`}>
                      {module.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{module.title}</h3>
                      <p className="text-sm text-gray-500">{module.description}</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-5">
                  {module.sections.map((section, idx) => (
                    <div key={idx} className="mb-4 last:mb-0">
                      <h4 className="font-medium text-gray-900 mb-2 text-sm">{section.title}</h4>
                      <ul className="space-y-1">
                        {section.steps.map((step, stepIdx) => (
                          <li key={stepIdx} className="flex items-start text-sm text-gray-600">
                            <div className="flex-shrink-0 w-4 h-4 bg-gray-100 rounded-full flex items-center justify-center mr-2 mt-0.5">
                              <span className="text-xs text-gray-500">{stepIdx + 1}</span>
                            </div>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Astuces rapides */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center mb-6">
                <HelpCircle className="w-5 h-5 text-gray-700 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Astuces rapides</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quickTips.map((tip, idx) => (
                  <div key={idx} className="flex items-start p-3 bg-gray-50 rounded">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                      <div className="text-blue-600">
                        {tip.icon}
                      </div>
                    </div>
                    <p className="text-sm text-gray-700">{tip.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div className="mt-6 bg-white rounded-lg border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Questions fréquentes</h2>
              <div className="space-y-6">
                {faqs.map((faq, idx) => (
                  <div key={idx}>
                    <h3 className="font-medium text-gray-900 mb-2">{faq.question}</h3>
                    <p className="text-sm text-gray-600">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contacts */}
            <div className="bg-white rounded-lg border p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Support technique</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Mail className="w-4 h-4 text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="text-sm font-medium">contact@mansouria-app.com</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Phone className="w-4 h-4 text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Téléphone</p>
                    <p className="text-sm font-medium">05 XX XX XX XX</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Disponibilité</p>
                    <p className="text-sm font-medium">8h-18h, Lundi-Vendredi</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer aide */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              © {new Date().getFullYear()} Plateforme de Gestion Communale - Tous droits réservés
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Version 1.0.0 | Dernière mise à jour: {new Date().toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Aide;